// Per-clinic business hours. Stored on the tenant as JSONB keyed by weekday
// (0=Sunday … 6=Saturday). Shared by the AdminPanel editor, the FullCalendar
// shading (businessHours prop), and the soft validation in EventModal.

export interface DayHours {
  closed?: boolean;
  open?: string;       // "HH:MM"
  close?: string;      // "HH:MM"
  lunchStart?: string; // "HH:MM" — optional midday break
  lunchEnd?: string;   // "HH:MM"
}

// Keys are weekday numbers as strings: "0" (Sunday) … "6" (Saturday).
export type BusinessHours = Record<string, DayHours>;

export const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

const HHMM = /^([01]?\d|2[0-3]):([0-5]\d)$/;

export const toMinutes = (hhmm?: string): number | null => {
  if (!hhmm) return null;
  const m = HHMM.exec(hhmm.trim());
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};

// True when the object holds at least one usable (open) day.
export const hasBusinessHours = (bh?: BusinessHours | null): boolean => {
  if (!bh || typeof bh !== 'object') return false;
  return WEEKDAYS.some(d => {
    const c = bh[String(d)];
    return c && !c.closed && toMinutes(c.open) != null && toMinutes(c.close) != null;
  });
};

// A blank week — every day closed. Used as the editor's starting point.
export const emptyBusinessHours = (): BusinessHours => {
  const bh: BusinessHours = {};
  for (const d of WEEKDAYS) bh[String(d)] = { closed: true };
  return bh;
};

// A sensible default: Mon–Fri 09:00–18:00 with a 12:00–13:00 lunch, weekend closed.
export const defaultBusinessHours = (): BusinessHours => {
  const bh = emptyBusinessHours();
  for (const d of [1, 2, 3, 4, 5]) {
    bh[String(d)] = { open: '09:00', close: '18:00', lunchStart: '12:00', lunchEnd: '13:00' };
  }
  return bh;
};

interface FCBusinessHours { daysOfWeek: number[]; startTime: string; endTime: string }

// Convert to the array shape FullCalendar's `businessHours` prop expects.
// A lunch break splits the day into two segments. Returns undefined when there
// is nothing usable to shade (caller should then omit the prop entirely).
export const toFullCalendarBusinessHours = (
  bh?: BusinessHours | null
): FCBusinessHours[] | undefined => {
  if (!hasBusinessHours(bh)) return undefined;
  const out: FCBusinessHours[] = [];
  for (const d of WEEKDAYS) {
    const c = bh![String(d)];
    if (!c || c.closed) continue;
    const open = toMinutes(c.open);
    const close = toMinutes(c.close);
    if (open == null || close == null || close <= open) continue;
    const ls = toMinutes(c.lunchStart);
    const le = toMinutes(c.lunchEnd);
    const hasLunch = ls != null && le != null && ls < le && ls >= open && le <= close;
    if (hasLunch) {
      out.push({ daysOfWeek: [d], startTime: c.open!, endTime: c.lunchStart! });
      out.push({ daysOfWeek: [d], startTime: c.lunchEnd!, endTime: c.close! });
    } else {
      out.push({ daysOfWeek: [d], startTime: c.open!, endTime: c.close! });
    }
  }
  return out.length ? out : undefined;
};

// Soft check used before saving an appointment. Returns true (allowed) when no
// hours are configured, so the legacy "schedule anytime" behaviour is preserved.
export const isWithinBusinessHours = (date: Date, bh?: BusinessHours | null): boolean => {
  if (!hasBusinessHours(bh)) return true;
  if (!(date instanceof Date) || isNaN(date.getTime())) return true;
  const c = bh![String(date.getDay())];
  if (!c || c.closed) return false;
  const open = toMinutes(c.open);
  const close = toMinutes(c.close);
  if (open == null || close == null) return false;
  const t = date.getHours() * 60 + date.getMinutes();
  if (t < open || t >= close) return false;
  const ls = toMinutes(c.lunchStart);
  const le = toMinutes(c.lunchEnd);
  if (ls != null && le != null && ls < le && t >= ls && t < le) return false;
  return true;
};
